// src/components/WaitlistView/WaitlistView.jsx
import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { FiUser, FiClock, FiCalendar, FiAlertCircle, FiMail, FiPhone } from 'react-icons/fi';
import { toast } from 'react-hot-toast';
import { getWaitlist, addToWaitlist, removeFromWaitlist, getWaitlistStats } from '../../services/waitlistService';
import { logActivity, ACTIVITY_TYPES } from '../../services/activityService';
import { useUser } from '../../context/UserContext';
import { useTenant } from '../../context/TenantContext';
import WaitlistModal from './WaitlistModal';

const WaitlistView = () => {
  const { user } = useUser();
  const { currentTenant, loading: tenantLoading } = useTenant();
  const navigate = useNavigate();
  const [waitlist, setWaitlist] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [editingEntry, setEditingEntry] = useState(null);

  // Load entries when tenant context is ready
  useEffect(() => {
    if (!tenantLoading && currentTenant) {
      loadWaitlist();
    } else if (!tenantLoading && !currentTenant) {
      setLoading(false);
    }
  }, [tenantLoading, currentTenant]);

  const loadWaitlist = async () => {
    try {
      setLoading(true);
      const [waitlistData, statsData] = await Promise.all([
        getWaitlist(),
        getWaitlistStats()
      ]);
      setWaitlist(waitlistData);
      setStats(statsData);
    } catch (error) {
      console.error('Error loading waitlist:', error);
      toast.error('Failed to load waitlist');
    } finally {
      setLoading(false);
    }
  };

  // Handle added entries (from modal)
  const handleAdded = async (savedEntry) => {
    try {
      if (editingEntry) {
        // Update existing entry in the list
        setWaitlist(waitlist.map(entry => 
          entry.id === editingEntry.id ? savedEntry : entry
        ));
        toast.success('Waitlist entry updated');
      } else {
        // Add new entry to the list
        setWaitlist([...waitlist, savedEntry]);
        toast.success('Client added to waitlist');
      }
      
      // Log activity
      await logActivity(
        editingEntry ? ACTIVITY_TYPES.WAITLIST_UPDATED : ACTIVITY_TYPES.WAITLIST_ADDED,
        { 
          clientName: savedEntry.clientName || savedEntry.name,
          priority: savedEntry.priority,
          preferredType: savedEntry.preferredType || 'Any'
        },
        user?.userId || user?.id,
        user?.role
      );
      
      setModalOpen(false);
      setEditingEntry(null);
      
      // Refresh stats
      const newStats = await getWaitlistStats();
      setStats(newStats);
    } catch (error) {
      console.error('Error processing waitlist entry:', error);
      toast.error('Failed to save waitlist entry');
    }
  };

  const handleEdit = (entry) => {
    setEditingEntry(entry);
    setModalOpen(true);
  };

  // Handle convert to appointment
  const handleConvertToAppointment = (waitlistEntry) => {
    navigate('/calendar', {
      state: {
        openAppointmentModal: true,
        waitlistData: {
          clientId: waitlistEntry.clientId,
          clientName: waitlistEntry.clientName,
          clientEmail: waitlistEntry.clientEmail,
          clientPhone: waitlistEntry.clientPhone,
          preferredType: waitlistEntry.preferredType,
          timePreferences: waitlistEntry.timePreferences,
          preferredStaff: waitlistEntry.preferredStaff,
          waitlistId: waitlistEntry.id,
          notes: waitlistEntry.notes,
          priority: waitlistEntry.priority
        }
      }
    });
  };

  // Delete a waitlist entry
  const handleDelete = async (id) => {
    const entryToDelete = waitlist.find(entry => entry.id === id);
    if (!entryToDelete) return;
    
    if (!window.confirm(`Remove ${entryToDelete.clientName || entryToDelete.name} from the waitlist?`)) return;
    
    try {
      const success = await removeFromWaitlist(id);
      if (success) {
        setWaitlist(waitlist.filter(entry => entry.id !== id));
        toast.success('Client removed from waitlist');
        
        // Refresh stats
        const newStats = await getWaitlistStats();
        setStats(newStats);
      } else {
        toast.error('Failed to remove from waitlist');
      }
    } catch (error) {
      console.error('Error removing from waitlist:', error);
      toast.error('Failed to remove from waitlist');
    }
  };

  // Get priority badge color
  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'urgent': return 'bg-red-100 text-red-800';
      case 'high': return 'bg-orange-100 text-orange-800';
      case 'normal': return 'bg-blue-100 text-blue-800';
      case 'low': return 'bg-gray-100 text-gray-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  // Format time preferences
  const formatTimePreferences = (timePreferences) => {
    if (!timePreferences) return 'Any time';
    const prefs = [];
    if (timePreferences.morning) prefs.push('Morning');
    if (timePreferences.afternoon) prefs.push('Afternoon');
    if (timePreferences.evening) prefs.push('Evening');
    return prefs.length > 0 ? prefs.join(', ') : 'Any time';
  };

  if (tenantLoading || loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-500">
            {tenantLoading ? 'Loading clinic context...' : 'Loading waitlist...'}
          </p>
        </div>
      </div>
    );
  }

  // If no tenant is available, show appropriate message
  if (!currentTenant) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <FiAlertCircle className="mx-auto h-12 w-12 text-gray-400 mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No clinic context</h3>
          <p className="text-gray-500">Please ensure you have access to a clinic to view the waitlist.</p>
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-6 p-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Waitlist Management</h1>
          <p className="text-gray-600 mt-1">Manage clients waiting for appointments</p>
        </div>
        <button
          onClick={() => setModalOpen(true)}
          className="btn-primary flex items-center space-x-2"
        >
          <FiUser className="h-4 w-4" />
          <span>Add to Waitlist</span>
        </button>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          <div className="card-modern p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Total Waiting</p>
                <p className="text-2xl font-bold text-gray-900 mt-2">{stats.waiting}</p>
              </div>
              <div className="p-3 rounded-lg bg-blue-500 bg-opacity-10">
                <FiClock className="h-6 w-6 text-blue-600" />
              </div>
            </div>
          </div>
          
          <div className="card-modern p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Appointments Created</p>
                <p className="text-2xl font-bold text-gray-900 mt-2">{stats.converted}</p>
              </div>
              <div className="p-3 rounded-lg bg-green-500 bg-opacity-10">
                <FiCalendar className="h-6 w-6 text-green-600" />
              </div>
            </div>
          </div>
          
          <div className="card-modern p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">High Priority</p>
                <p className="text-2xl font-bold text-gray-900 mt-2">{stats.byPriority.urgent + stats.byPriority.high}</p>
              </div>
              <div className="p-3 rounded-lg bg-orange-500 bg-opacity-10">
                <FiAlertCircle className="h-6 w-6 text-orange-600" />
              </div>
            </div>
          </div>
          
          <div className="card-modern p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Average Wait Time</p>
                <p className="text-2xl font-bold text-gray-900 mt-2">{stats.averageWaitDays || 0} days</p>
              </div>
              <div className="p-3 rounded-lg bg-purple-500 bg-opacity-10">
                <FiClock className="h-6 w-6 text-purple-600" />
              </div>
            </div>
          </div>
        </div>
      )}

      {modalOpen && (
        <WaitlistModal
          isOpen={modalOpen}
          onClose={() => {
            setModalOpen(false);
            setEditingEntry(null);
          }}
          onAdded={handleAdded}
          editingEntry={editingEntry}
        />
      )}

      {/* Waitlist Table */}
      <div className="card-modern overflow-hidden">
        {waitlist.length === 0 ? (
          <div className="text-center py-12">
            <FiClock className="mx-auto h-12 w-12 text-gray-400 mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No clients waiting</h3>
            <p className="text-gray-500 mb-6">Add clients to the waitlist to get started.</p>
            <button
              onClick={() => setModalOpen(true)}
              className="btn-primary"
            >
              Add First Client
            </button>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Client
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Priority
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Preferences
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Contact
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Added
                  </th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {waitlist.map(entry => (
                  <tr key={entry.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="flex-shrink-0 h-10 w-10">
                          <div className="h-10 w-10 rounded-full bg-gray-300 flex items-center justify-center">
                            <FiUser className="h-5 w-5 text-gray-600" />
                          </div>
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-medium text-gray-900">
                            {entry.clientName || entry.name}
                          </div>
                          {entry.clientId && (
                            <Link
                              to={`/clients/${entry.clientId}`}
                              className="text-sm text-blue-600 hover:text-blue-900"
                            >
                              View Profile
                            </Link>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getPriorityColor(entry.priority)}`}>
                        {entry.priority || 'normal'}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-900">
                        <div>Type: {entry.preferredType || 'Any'}</div>
                        <div>Time: {formatTimePreferences(entry.timePreferences)}</div>
                        {entry.preferredStaff && (
                          <div>Staff: {entry.preferredStaff}</div>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-900">
                        {entry.email && (
                          <div className="flex items-center space-x-1">
                            <FiMail className="h-3 w-3" />
                            <span>{entry.email}</span>
                          </div>
                        )}
                        {entry.phone && (
                          <div className="flex items-center space-x-1">
                            <FiPhone className="h-3 w-3" />
                            <span>{entry.phone}</span>
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {entry.createdAt ? new Date(entry.createdAt).toLocaleDateString() : 'Unknown'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-center">
                      <div className="flex items-center justify-center space-x-2">
                        <button
                          onClick={() => handleConvertToAppointment(entry)}
                          className="text-green-600 hover:text-green-900 text-sm font-medium"
                        >
                          Create Appointment
                        </button>
                        <button
                          onClick={() => handleEdit(entry)}
                          className="text-blue-600 hover:text-blue-900 text-sm font-medium"
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => handleDelete(entry.id)}
                          className="text-red-600 hover:text-red-900 text-sm font-medium"
                        >
                          Remove
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default WaitlistView;
